import CondorSpinner from '../CondorSpinner';
import React, { useEffect, useMemo, useState } from 'react';
import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import Tooltip from '@mui/material/Tooltip';
import { useRoleAuthorization } from '../../hooks/RoleAuthorization';
import { OnSubmitModal } from '../../Pages/OnSubmitModal';
import { useFormattedDate } from '../../hooks/useFormattedDate';
import { useMutation, useQuery } from '@tanstack/react-query';
import { cancelIoTJob, getIoTJobStatus } from '../../services/IoTService';

interface IoTJobDetailsProps {
  canopyDeviceId: string;
  jobId: string;
}

export const IotJobDetails = (props: IoTJobDetailsProps) => {
  const { canopyDeviceId, jobId } = props;

  const { formattedDate } = useFormattedDate();
  const {
    data,
    isPending: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['iotJobStatus', canopyDeviceId, jobId],
    queryFn: () => getIoTJobStatus({ canopyDeviceId, jobId }),
  });

  const [isButtonVisible, setIsButtonVisible] = useState<boolean>(false);
  const { canCancelJob } = useRoleAuthorization();
  const [modalState, setModalState] = useState({
    isVisible: false,
    title: '',
    message: '',
  });
  const iotJobStatus = useMemo(() => {
    return data
      ? {
          status: data.status,
          latestUpdatedAt: data.lastUpdatedAt,
          isCancellable: data.isCancellable,
        }
      : {
          status: 'unknown',
          latestUpdatedAt: 'unknown',
        };
  }, [data]);

  useEffect(() => {
    if (iotJobStatus.isCancellable && canCancelJob()) {
      setIsButtonVisible(true);
    }
  }, [data, canCancelJob]);

  const { mutate, isPending: cancelIotJobLoading } = useMutation({
    mutationFn: cancelIoTJob,
    onError: (err) => {
      setModalState({
        isVisible: true,
        title: 'Error',
        message: err.message,
      });
    },
    onSuccess: async (response) => {
      await refetch();
      setModalState({
        isVisible: true,
        title: 'Success',
        message: response.message ?? 'Cancel IoT Job Success',
      });
    },
  });

  const handleCancelIotJobExecution = async () => {
    mutate({ canopyDeviceId, jobId });
    setIsButtonVisible(false);
  };
  const lastUpdatedAt = (): string => {
    const processedDate = iotJobStatus
      ? formattedDate(iotJobStatus.latestUpdatedAt)
      : null;
    return processedDate
      ? `Last updated at: ${processedDate}`
      : 'Not known when status was updated';
  };
  const handleCloseModal = async () => {
    setModalState({ isVisible: false, title: '', message: '' });
    await refetch();
    setIsButtonVisible(false);
  };

  return (
    <>
      {loading ? (
        <CondorSpinner />
      ) : (
        <div style={{ display: 'flex' }}>
          <div style={{ flexBasis: '40%' }}>
            <p style={{ fontSize: '1em', fontWeight: 'bolder' }}>
              Latest Iot Job Status
            </p>
          </div>
          {error ? (
            <div style={{ display: 'flex' }}>
              <div style={{ flexBasis: '20%' }}></div>
              <div
                style={{
                  flexBasis: '80%',
                  paddingLeft: '20px',
                  color: 'red',
                  fontWeight: 'bold',
                }}
              >
                {error.message}
              </div>
            </div>
          ) : (
            <div style={{ flexBasis: '20%' }}>
              {iotJobStatus && (
                <Tooltip title={lastUpdatedAt()} placement="top">
                  <p style={{ fontSize: '1em' }}>{iotJobStatus.status} </p>
                </Tooltip>
              )}
            </div>
          )}
          <div style={{ flexBasis: '20%' }}>
            {isButtonVisible && (
              <StyledButton
                disabled={cancelIotJobLoading}
                onPress={() => handleCancelIotJobExecution()}
                buttonRules={ButtonRules.primaryA}
              >
                Cancel Job
              </StyledButton>
            )}
          </div>
          <OnSubmitModal
            isVisible={modalState.isVisible}
            onClose={handleCloseModal}
            title={modalState.title}
            message={modalState.message}
          />
        </div>
      )}
    </>
  );
};
